import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { updateStory, createStory, setMemoryCache, clearMemoryCache } from '../../services/MemoriesAPI/memoriesAPI'
import { useStoryManager } from '../../providers/StoryProvider'

const updateOrCreateMemory = async (id, title, text, updateMem) => {
  const response = updateMem ? await updateStory(id, text, title) : await createStory({ text, title })
  clearMemoryCache(response.id)
  setMemoryCache(response.id, response)

  return response.id
}

export default function useSaveStoryLogic({
  pageNumber,
  positionIndex,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
}) {
  const { updatePageContent } = useMemoryBook()
  const { pendingEdits } = useStoryManager()

  const { storyId, title, description } = pendingEdits
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()

  // Check if this is a custom layout element
  const isCustomLayoutElement = Boolean(customLayoutElementId && onUpdateCustomLayoutElementData)

  const handleSave = async (updateMemory = false) => {
    try {
      const memoryId = await updateOrCreateMemory(storyId, title, description, updateMemory)
      const updatedId = memoryId || storyId

      // For custom layout elements, use the custom update function
      if (isCustomLayoutElement) {
        await onUpdateCustomLayoutElementData({ memoryId: updatedId, isPlaceholder: undefined })
      } else {
        // For regular page content, use the standard update function
        await updatePageContent(pageNumber, positionIndex, { memoryId: updatedId })
      }
    } catch (error) {
      console.error('Error saving story:', error)
      showStatusOverlay({
        message: t(
          'TextPickerOverlay.notSaved.description',
          'Something went wrong! Unable to save or update story! Please try again later.'
        ),
        type: 'error',
      })
    }
  }

  const saveEditStory = async () => {
    // If switching between gallery stories, just update the content otherwsie save the story to memories
    if (pendingEdits?.isGalleryStory && pendingEdits?.storyId) {
      clearMemoryCache(storyId)

      // For custom layout elements, use the custom update function
      if (isCustomLayoutElement) {
        await onUpdateCustomLayoutElementData({ memoryId: storyId, isPlaceholder: undefined })
      } else {
        // For regular page content, use the standard update function
        await updatePageContent(pageNumber, positionIndex, { memoryId: storyId })
      }
      return
    }
    await handleSave()
  }

  const saveEditedGalleryMemory = async (updateStoryMemory = false) => {
    await handleSave(updateStoryMemory)
  }

  return { saveEditStory, saveEditedGalleryMemory }
}
